import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import fetchAllCentreManager from "../List/CentreMangerList";
import GlobalDelete from "../../components/common/GlobalDelete";
import AddRegister from "./Add/AddRegister";
import AddBreak from "./Add/AddBreak";
import AddClass from "./Add/AddClass";
import AddPackage from "./Add/AddPackage";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "CENTER";

/* ================= CSV EXPORT HELPER ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "Centre_List.csv",
) => {
  if (!rows.length) {
    toast.warning("No data to export");
    return;
  }

  const visibleColumns = table
    .getAllLeafColumns()
    .filter((col) => col.getIsVisible() && col.columnDef.accessorKey);

  const headers = visibleColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    visibleColumns.map((col) => {
      const value = row[col.columnDef.accessorKey];
      return `"${value ?? ""}"`;
    }),
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ==================================================== */

const Center = ({ handleCenterChanged }) => {
  const [filters, setFilters] = useState({
    centerName: "",
    centerCode: "",
    email: "",
    centerManagerId: "",
  });
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const [centerManagerData, setCenterManagerData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [show, setShow] = useState(false);
  const toggleDropdown = () => setShow(!show);

  const canEdit = Boolean(storedScreens?.centerListingUpdate);
  const canDelete = Boolean(storedScreens?.centerListingDelete);
  const canRead = Boolean(storedScreens?.centerListingRead);
  const showActionColumn = canEdit || canDelete;

  const defaultColumnVisibility = {
    gst: false,
    address: false,
    bankAccountName: false,
    bankAccountNumber: false,
    bankBranch: false,
    bankName: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
    invoiceNotes: false,
    openingDate: false,
    taxRegistrationNumber: false,
    zipCode: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: "serialNo",
        header: "S.NO",
        size: 40,
        Cell: ({ row }) => row.index + 1,
      },
      { accessorKey: "centerName", header: "Centre Name" },
      { accessorKey: "centerManager", header: "Centre Manager" },
      { accessorKey: "code", header: "Code" },
      { accessorKey: "uenNumber", header: "UEN Number" },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "mobile", header: "Mobile" },
      { accessorKey: "address", header: "Address" },
      { accessorKey: "invoiceNotes", header: "Invoice Notes" },
      { accessorKey: "openingDate", header: "Opening Date" },
      { accessorKey: "bankAccountName", header: "Bank A/C Name" },
      { accessorKey: "bankAccountNumber", header: "Bank A/C Number" },
      { accessorKey: "bankBranch", header: "Bank Branch" },
      { accessorKey: "bankName", header: "Bank Name" },
      { accessorKey: "gst", header: "GST" },
      { accessorKey: "taxRegistrationNumber", header: "Tax Reg Number" },
      { accessorKey: "zipCode", header: "Zip Code" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions", // display column
      header: "",
      size: 20,
      enableSorting: false,
      enableColumnActions: true,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };
    return [
      baseColumns[0], // S.NO
      actionColumn, // Actions
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  const fetchCenterManagerData = async () => {
    try {
      const centerManagerData = await fetchAllCentreManager();
      setCenterManagerData(centerManagerData);
    } catch (error) {
      toast.error(error?.message || "Error loading center managers");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const nonEmptyFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== ""),
      );

      const queryParams = Object.keys(nonEmptyFilters).length
        ? `?${new URLSearchParams(nonEmptyFilters).toString()}`
        : "";

      const response = await api.get(`/getCenterWithCustomInfo${queryParams}`);
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load centers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenterManagerData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({
      centerName: "",
      centerCode: "",
      email: "",
      centerManagerId: "",
    });
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a",
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  /* ---------------- MRT TABLE INSTANCE ---------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/center/view/${row.original.id}`)
        : undefined,
      style: {
        cursor: canRead ? "pointer" : "default",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, data)}
      >
        Export CSV
      </button>
    ),
  });

  return (
    <div className="container-fluid px-2 my-4 center">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Centre Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Centre Listing
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Centre</strong>
          </span>
        </div>

        <div className="d-flex justify-content-between">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="centerName"
                value={filters.centerName}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Centre Name"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="centerCode"
                value={filters.centerCode}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Code"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Email"
                autoComplete="off"
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <select
                name="centerManagerId"
                value={filters.centerManagerId}
                onChange={handleFilterChange}
                className="form-select form-select-sm center_list"
                style={{ width: "100%" }}
              >
                <option value="">Select Centre Manager</option>
                {centerManagerData.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.userNames}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-2 ms-2">
              <button
                type="button"
                onClick={clearFilter}
                className="btn btn-sm btn-border"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Dropdown Toggle Button */}
        <div className="d-flex justify-content-end">
          {storedScreens?.centerListingCreate && (
            <>
              <button
                className="btn btn-button btn-sm me-2 mb-2 dropdown-toggle"
                type="button"
                id="dropdownMenuButton"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded={show ? "true" : "false"}
                onClick={toggleDropdown}
                style={{
                  fontWeight: "600",
                  position: "relative",
                  right: 0,
                }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>

              <div
                className={`dropdown-menu ${show ? "show" : ""}`}
                style={{
                  position: "absolute",
                  top: "8rem",
                  zIndex: "100",
                }}
                aria-labelledby="dropdownMenuButton"
              >
                <Link to="/center/add" className="dropdown-item btn">
                  <button
                    className="dropdown-item btn"
                    onClick={() => {
                      setShow(false);
                    }}
                  >
                    Add Center
                  </button>
                </Link>
                <button
                  className="dropdown-item btn"
                  onClick={() => {
                    setShow(false);
                  }}
                >
                  <AddRegister onSuccess={fetchData} />
                </button>
                <button
                  className="dropdown-item btn"
                  onClick={() => {
                    setShow(false);
                  }}
                >
                  <AddClass onSuccess={fetchData} />
                </button>
                <button
                  className="dropdown-item btn"
                  onClick={() => {
                    setShow(false);
                  }}
                >
                  <AddPackage onSuccess={fetchData} />
                </button>
                <button
                  className="dropdown-item btn"
                  onClick={() => {
                    setShow(false);
                  }}
                >
                  <AddBreak onSuccess={fetchData} />
                </button>
              </div>
            </>
          )}
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              disableScrollLock
            >
              {/* <MenuItem >
                <AddRegister id={selectedId} onSuccess={fetchData} handleMenuClose={handleMenuClose}/>
              </MenuItem>
              <MenuItem >
                <AddClass id={selectedId} onSuccess={fetchData} handleMenuClose={handleMenuClose}/>
              </MenuItem>
              <MenuItem >
                <AddPackage id={selectedId} onSuccess={fetchData} handleMenuClose={handleMenuClose}/>
              </MenuItem>
              <MenuItem >
                <AddBreak id={selectedId} onSuccess={fetchData} handleMenuClose={handleMenuClose}/>
              </MenuItem> */}
              {canEdit && (
                <MenuItem
                  onClick={() => navigate(`/center/edit/${selectedId}`)}
                  className="text-start mb-0 menuitem-style"
                >
                  Edit
                </MenuItem>
              )}

              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteCenter/${selectedId}`}
                    onDeleteSuccess={fetchData}
                    onOpen={handleMenuClose}
                    // deleteCenterData={true}
                    handleCenterChanged={handleCenterChanged}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Center;
